import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Badge from 'components/UI/Badge';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/checkout/chevron-right.svg';

const getContainerClasses = ({ selected, recommended, disabled }) => {
  let border = 'border-transparent bg-white';
  if (selected) border = 'border-primary-700 bg-white';
  else if (recommended) border = 'border-gold-300 bg-gold-50';
  return `${border} ${disabled ? 'opacity-50' : ''}`;
};

const MethodCard = ({
  provider,
  logo,
  eyebrow,
  title,
  description = null,
  note = null,
  badge = null,
  selected = false,
  disabled = false,
  recommended = false,
  expandable = false,
  onSelect,
  children = null,
}) => {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const wasSelected = useRef(selected);

  // When this method becomes the selected one (by click or preselection), it becomes the
  // active element and is centered on screen with its details/stores expanded.
  useEffect(() => {
    if (selected && !wasSelected.current) {
      buttonRef.current?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
    wasSelected.current = selected;
  }, [selected]);

  const handleClick = () => {
    if (disabled || selected) return;
    onSelect(provider);
  };
  const expanded = expandable && selected;

  return (
    <div
      ref={containerRef}
      data-provider={provider}
      className={`overflow-hidden rounded-2xl border-[1.5px] border-solid shadow-card transition-colors ${getContainerClasses({ selected, recommended, disabled })}`}
    >
      <button
        ref={buttonRef}
        type='button'
        role='radio'
        aria-checked={selected}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={handleClick}
        className={`flex w-full items-center gap-3 border-0 bg-transparent p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className='flex w-16 shrink-0 items-center justify-center sm:w-24'>
          {logo}
        </span>
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='text-[13px] text-neutral-600'>{eyebrow}</span>
          <span className='flex flex-wrap items-center justify-between gap-x-2 gap-y-1'>
            <span className='mt-1 text-base font-semibold text-neutral-900'>{title}</span>
            {badge && <Badge variant='gold'>{badge}</Badge>}
          </span>
          {description && <span className='mt-2 text-[13px] text-neutral-500'>{description}</span>}
          {note && <span className='mt-1 text-[13px] text-neutral-500'>{note}</span>}
        </span>
        <ChevronRightIcon
          aria-hidden='true'
          className={`h-6 w-6 shrink-0 transition-transform ${expanded ? '-rotate-90 text-primary-700' : 'text-gold-500'}`}
        />
      </button>
      {children && <div className='px-5 pb-5'>{children}</div>}
    </div>
  );
};

MethodCard.propTypes = {
  provider: PropTypes.string.isRequired,
  logo: PropTypes.node.isRequired,
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  note: PropTypes.string,
  badge: PropTypes.string,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  recommended: PropTypes.bool,
  expandable: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default MethodCard;
